import type { DrawerState, DrawerPosition } from '@app/shared/types/drawer'
import { RightDrawerContent } from '@app/shared/types/drawer'

import debounce from 'lodash/debounce'

const collapseDrawer = (
  drawer: HTMLElement,
  modifier: DrawerPosition
): void => {
  window.requestAnimationFrame(() => {
    drawer.classList.remove(`sf-sidebar__expanded--${modifier}`)
    drawer.classList.add(`sf-sidebar__collapsed--${modifier}`)
  })
}

const expandDrawer = (drawer: HTMLElement, modifier: DrawerPosition): void => {
  window.requestAnimationFrame(() => {
    drawer.classList.remove(`sf-sidebar__collapsed--${modifier}`)
    drawer.classList.add(`sf-sidebar__expanded--${modifier}`)
  })
}

export default (doc: Document): Array<() => void> => {
  const unsubscribers: Array<() => void> = []

  const leftDrawer = doc.getElementById('left-drawer')
  const rightDrawer = doc.getElementById('right-drawer')
  const mainContent = doc.getElementById('main-content')

  const hasAnyDrawer = leftDrawer || rightDrawer

  if (!hasAnyDrawer) {
    return []
  }

  // Stop animating the main content after the animation ends
  // to prevent the main content to be animated when the window is resized
  const handleMainContentClass = (): void => {
    mainContent?.classList.remove('two-drawer-layout__main--transition')
  }

  const addAnimationListeners = (drawer: HTMLElement): void => {
    const emitAnimationEndEvent = debounce(
      makeEmitAnimationEndEvent(drawer),
      300,
      {
        leading: true,
        trailing: false,
      }
    )

    const emitAnimationStartEvent = debounce(
      makeEmitAnimationStartEvent(drawer),
      300,
      {
        leading: true,
        trailing: false,
      }
    )

    drawer.addEventListener('transitionstart', emitAnimationStartEvent)
    drawer.addEventListener('transitionend', emitAnimationEndEvent)
    drawer.addEventListener('transitionend', handleMainContentClass)

    unsubscribers.push(() => {
      emitAnimationEndEvent.cancel()
      emitAnimationStartEvent.cancel()
      drawer.removeEventListener('transitionstart', emitAnimationStartEvent)
      drawer.removeEventListener('transitionend', emitAnimationEndEvent)
      drawer.removeEventListener('transitionend', handleMainContentClass)
    })
  }

  const makeEmitAnimationEndEvent =
    (drawer: HTMLElement) =>
    (e: Event): void => {
      if (e.target === drawer) {
        window.eventHub.$emit('drawer:animation-end')
      }
    }

  const makeEmitAnimationStartEvent =
    (drawer: HTMLElement) =>
    (e: Event): void => {
      if (e.target === drawer) {
        window.eventHub.$emit('drawer:animation-start')
      }
    }

  if (leftDrawer) {
    addAnimationListeners(leftDrawer)
  }

  if (rightDrawer) {
    addAnimationListeners(rightDrawer)
  }

  const isDrawerCollapsed = (
    drawer: HTMLElement | null,
    position: DrawerPosition
  ): boolean => {
    if (!drawer) {
      return false
    }

    return drawer.classList.contains(`sf-sidebar__collapsed--${position}`)
  }

  const closeDrawer = (position: DrawerPosition): void => {
    const drawer = position === 'left' ? leftDrawer : rightDrawer

    if (!drawer || isDrawerCollapsed(drawer, position)) {
      return
    }

    mainContent?.classList.add('two-drawer-layout__main--transition')

    if (!hasAnyDrawer) {
      mainContent?.classList.remove('two-drawer-layout__main--sidebar-expanded')
    }

    collapseDrawer(drawer, position)
  }

  const openDrawer = (position: DrawerPosition): void => {
    const drawer = position === 'left' ? leftDrawer : rightDrawer

    if (!drawer || !isDrawerCollapsed(drawer, position)) {
      return
    }

    mainContent?.classList.add('two-drawer-layout__main--transition')
    mainContent?.classList.add('two-drawer-layout__main--sidebar-expanded')

    expandDrawer(drawer, position)
  }

  const handleDrawerOpen = (drawer: DrawerState): void => {
    if (drawer.direction !== 'left' && drawer.direction !== 'right') {
      return
    }

    const oppositeDrawer = drawer.direction === 'left' ? 'right' : 'left'

    openDrawer(drawer.direction)
    closeDrawer(oppositeDrawer)

    if (mainContent && rightDrawer) {
      window.requestAnimationFrame(() => {
        if (
          drawer.content === RightDrawerContent.FileAttachments ||
          drawer.content === RightDrawerContent.FilePreview
        ) {
          mainContent.classList.add(
            'two-drawer-layout__main--with-file-attachments-sidebar'
          )
          rightDrawer.classList.add('sf-sidebar__file-attachments')
        } else {
          mainContent.classList.remove(
            'two-drawer-layout__main--with-file-attachments-sidebar'
          )
          rightDrawer.classList.remove('sf-sidebar__file-attachments')
        }
      })
    }
  }

  const handleDrawerClose = (): void => {
    closeDrawer('left')
    closeDrawer('right')

    mainContent?.classList.remove('two-drawer-layout__main--sidebar-expanded')
    mainContent?.classList.remove(
      'two-drawer-layout__main--with-file-attachments-sidebar'
    )
  }

  window.eventHub.$on('drawer:open', handleDrawerOpen)
  window.eventHub.$on('drawer:close', handleDrawerClose)

  window.eventHub.$emit('drawer:ready')

  unsubscribers.push(() => {
    window.eventHub.$off('drawer:open', handleDrawerOpen)
    window.eventHub.$off('drawer:close', handleDrawerClose)
  })

  return unsubscribers
}
